import { NOTIFICATION_TITLES } from '@nexus/core';
import { adminDe } from './admin.de';
import { catalogsDe } from './catalogs.de';
import { documentsDe } from './documents.de';
import { findingsDe } from './findings.de';
import { plansDe } from './plans.de';
import { questionnairesDe } from './questionnaires.de';
import { signatureDe } from './signature.de';

/**
 * German texts of the kernel. English in en.ts with exactly the same keys (checked by a test).
 * Modules bring their own texts below their module ID.
 */
export const de = {
  app: { name: 'STAW App' },
  ui: {
    close: 'Schließen',
    loading: 'Wird geladen',
    moreActions: 'Weitere Aktionen',
    noResults: 'Keine Treffer',
    required: 'Pflichtfeld',
    optional: 'optional',
    clear: 'Leeren',
    selectAll: 'Alle auswählen',
    selectRow: 'Zeile auswählen',
    sortAscending: 'Aufsteigend sortieren',
    sortDescending: 'Absteigend sortieren',
    previousPage: 'Vorherige Seite',
    nextPage: 'Nächste Seite',
    retry: 'Erneut versuchen',
    confirm: 'Bestätigen',
    cancel: 'Abbrechen',
    breadcrumbs: 'Pfad',
    filters: 'Filter',
    resetFilters: 'Filter zurücksetzen',
    steps: 'Schritte',
    stepCompleted: 'erledigt',
    back: 'Zurück',
    next: 'Weiter',
  },
  common: {
    back: 'Zurück',
    erasedPerson: 'Gelöschte Person',
    save: 'Speichern',
    saved: 'Gespeichert',
    reset: 'Zurücksetzen',
    signOut: 'Abmelden',
    saveFailed: 'Nicht gespeichert. Bitte erneut versuchen.',
    cancel: 'Abbrechen',
    create: 'Anlegen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    add: 'Hinzufügen',
    remove: 'Entfernen',
    actions: 'Aktionen',
    loadFailed: 'Konnte nicht geladen werden. Verbindung prüfen und erneut versuchen.',
    relative: {
      justNow: 'gerade eben',
    },
  },
  shell: {
    skipToContent: 'Zum Inhalt springen',
    navigation: 'Hauptnavigation',
    home: 'Startseite',
    menu: 'Menü',
    bottomBar: 'Schnellzugriff',
    collapseSidebar: 'Navigation einklappen',
    expandSidebar: 'Navigation ausklappen',
    expandGroup: 'Seiten von „{{name}}“ aufklappen',
    collapseGroup: 'Seiten von „{{name}}“ zuklappen',
    modules: 'Module',
    admin: 'Verwaltung',
    help: 'Anleitungen',
    forMe: 'Für mich',
    forMeCount_one: '{{count}} wartet',
    forMeCount_other: '{{count}} warten',
    moduleSettings: 'Einstellungen',
    refreshed: 'Aktualisiert',
    pushPrompt: {
      title: 'Mitteilungen erlauben?',
      description:
        'Die STAW App meldet neue Benachrichtigungen auf diesem Gerät, auch wenn sie geschlossen ist.',
      hint: 'Die Mitteilung nennt nur die Zahl neuer Benachrichtigungen, nie deren Inhalt. Im Profil lässt sich das jederzeit ändern.',
      allow: 'Erlauben',
      later: 'Später',
    },
    update: {
      available: 'Eine neue Version der STAW App ist da. Der nächste Seitenwechsel lädt sie.',
      reload: 'Neu laden',
      later: 'Später',
    },
    search: {
      open: 'Suchen',
      label: 'Suche',
      placeholder: 'Personen, Datensätze, Seiten …',
      empty: 'Nichts gefunden.',
      pages: 'Seiten',
      settings: 'Einstellungen',
      actions: 'Aktionen',
      navigate: 'auswählen',
      select: 'öffnen',
      shortcut: 'Strg K',
      dismiss: 'schließen',
      loading: 'Suche läuft …',
      recent: 'Zuletzt geöffnet',
      guides: 'Anleitungen',
      showAll: 'Alle Treffer für „{{term}}“',
      kinds: { account: 'Personen', group: 'Gruppen', core: 'Verwaltung' },
    },
    company: {
      switch: 'Firma wechseln',
      title: 'Firma',
      active: 'Aktiv',
      switchFailed: 'Firmenwechsel fehlgeschlagen. Bitte erneut versuchen.',
      noOther: 'Keine weitere Firma verknüpft.',
    },
    sync: {
      title: 'Synchronisierung',
      states: {
        synced: 'Synchron',
        syncing: 'Synchronisiert …',
        offline: 'Offline',
        pending_one: '{{count}} Änderung wartet',
        pending_other: '{{count}} Änderungen warten',
        attention: 'Bitte prüfen',
      },
      connection: 'Verbindung',
      online: 'Online',
      offline: 'Keine Verbindung',
      lastSync: 'Letzter Abgleich',
      never: 'noch nicht',
      pending: 'Wartende Änderungen',
      pendingNone: 'Keine wartenden Änderungen.',
      rejected: 'Abgelehnte Änderungen',
      syncNow: 'Jetzt synchronisieren',
      offlineData: 'Offline verfügbar',
      offlineDataNone: 'Noch keine Daten. Module legen fest, was offline nutzbar ist.',
      pendingCount_one: '{{count}} Änderung wartet auf die Übertragung.',
      pendingCount_other: '{{count}} Änderungen warten auf die Übertragung.',
      recordCount_one: '{{count}} Eintrag',
      recordCount_other: '{{count}} Einträge',
      notSynced: 'Nicht synchronisiert',
      conflict: 'Auf dem Server inzwischen geändert',
      compare: 'Vergleichen',
      discard: 'Verwerfen',
      compareTitle: 'Änderung vergleichen',
      compareDescription:
        'Der Eintrag wurde auf dem Server geändert, während dieses Gerät offline war. Welcher Stand soll gelten?',
      deletedOnServer:
        'Der Eintrag wurde auf dem Server gelöscht, während dieses Gerät offline war. Die Änderung lässt sich nicht mehr übertragen.',
      field: 'Feld',
      mine: 'Auf diesem Gerät',
      server: 'Auf dem Server',
      differs: 'abweichend',
      keepMine: 'Meine Änderung übernehmen',
      keepServer: 'Server-Stand behalten',
      keptMine: 'Die Änderung wird erneut übertragen.',
      keptServer: 'Der Server-Stand gilt.',
      signOutPendingTitle: 'Nicht übertragene Änderungen',
      signOutPending_one:
        '{{count}} Änderung oder Datei auf diesem Gerät ist noch nicht auf dem Server. Beim Abmelden wird sie gelöscht.',
      signOutPending_other:
        '{{count}} Änderungen oder Dateien auf diesem Gerät sind noch nicht auf dem Server. Beim Abmelden werden sie gelöscht.',
      signOutAnyway: 'Trotzdem abmelden',
      uploads: 'Dateien zum Hochladen',
      uploadWaiting: 'Wartet auf den Upload',
      uploadWaitingBecause: 'Wartet: {{reason}}',
      uploading: 'Wird hochgeladen …',
      retryUpload: 'Erneut senden',
      device: 'Auf diesem Gerät',
      files: 'Dateien',
      fileCount_one: '{{count}} Datei, {{size}}',
      fileCount_other: '{{count}} Dateien, {{size}}',
      filesNone: 'Keine',
      prepared: 'Vorbereitet',
      storage: 'Speicher',
      storageUsed: '{{used}} von {{quota}} belegt',
      storageFull:
        'Der Speicher des Geräts ist voll. Nicht alle Dateien vorbereiteter Einträge sind offline verfügbar.',
      clear: 'Offline-Daten löschen',
      clearTitle: 'Offline-Daten von diesem Gerät löschen?',
      clearDescription:
        'Alle Einträge, Dateien und Vorbereitungen dieser Firma werden von diesem Gerät gelöscht. Auf dem Server bleibt alles erhalten. Kataloge und Vorlagen lädt die App mit Verbindung neu, vorbereitete Einträge erst nach erneutem Vorbereiten. Ohne Verbindung startet die App erst wieder nach dem nächsten Abgleich.',
      clearUnsent_one:
        '{{count}} Änderung oder Datei ist noch nicht auf dem Server und geht dabei verloren.',
      clearUnsent_other:
        '{{count}} Änderungen oder Dateien sind noch nicht auf dem Server und gehen dabei verloren.',
      clearWord: 'LÖSCHEN',
      clearWordLabel: 'Zum Bestätigen LÖSCHEN eingeben',
      clearConfirm: 'Löschen',
      cleared: 'Offline-Daten gelöscht',
      clearFailed: 'Löschen fehlgeschlagen. Bitte erneut versuchen.',
      kernel: {
        files: 'Dateilisten',
        findings: 'Mängel',
        comments: 'Kommentare',
        people: 'Personenverzeichnis',
        catalogs: 'Kataloge (z. B. Mängelkatalog)',
        plans: 'Pläne',
        questionnaireTemplates: 'Fragebogen-Vorlagen',
        signature: 'Eigene Unterschrift',
        other: 'Weitere Daten',
      },
      prepare: {
        action: 'Offline verfügbar machen',
        preparing: 'Wird vorbereitet …',
        progress: '{{done}} von {{total}} Dateien auf dem Gerät',
        needsConnection: 'Zum Vorbereiten wird eine Verbindung gebraucht.',
        ready: 'Offline verfügbar',
        incomplete: 'Offline verfügbar, {{done}} von {{total}} Dateien',
        waiting: 'Noch nicht auf dem Gerät, kommt mit dem nächsten Abgleich',
        retry: 'Erneut versuchen',
        details: 'Stand {{when}}, {{size}}',
        detailsNoFiles: 'Stand {{when}}',
        auto: 'Automatisch vorbereitet',
        keep: 'Offline behalten',
        kept: 'Bleibt offline verfügbar, bis Sie es entfernen',
        remove: 'Dateien vom Gerät entfernen',
        removeTitle: 'Dateien vom Gerät entfernen?',
        removeDescription:
          'Die Dateien dieses Eintrags werden von diesem Gerät gelöscht und sind ohne Verbindung nicht mehr verfügbar. Auf dem Server bleibt alles erhalten.',
        removeConfirm: 'Entfernen',
        removed: 'Dateien vom Gerät entfernt',
        failed: 'Vorbereiten fehlgeschlagen. Bitte mit Verbindung erneut versuchen.',
      },
      reasons: {
        forbidden: 'Keine Berechtigung (mehr)',
        'not-found': 'Eintrag nicht gefunden',
        exists: 'Eintrag gibt es schon',
        'too-old': 'Zu lange offline, die Änderung ist verfallen',
        'invalid-time': 'Die Uhrzeit des Geräts stimmt nicht',
        'command-reused': 'Bereits mit anderem Inhalt übertragen',
        expired: 'Offline-Daten abgelaufen, Inhalt vom Gerät gelöscht',
        invalid: 'Ungültige Eingabe',
        'file-too-large': 'Datei zu groß',
        'file-type-not-allowed': 'Dateityp hier nicht erlaubt',
        'file-content-mismatch': 'Inhalt passt nicht zur Dateiendung',
        'file-infected': 'Schadsoftware gefunden',
        'file-exists': 'Datei gibt es schon mit anderem Inhalt',
        'file-name-invalid': 'Ungültiger Dateiname',
        'file-missing': 'Datei auf dem Gerät nicht mehr lesbar',
        'scanner-unavailable': 'Virenscan gerade nicht erreichbar',
        'files-unavailable': 'Dateispeicher gerade nicht erreichbar',
        'finding-catalog-invalid': 'Katalogeintrag nicht (mehr) verfügbar',
        'finding-plan-invalid': 'Plan nicht (mehr) verfügbar',
        'finding-photo-invalid': 'Foto gehört nicht zu diesem Mangel',
        'finding-source-invalid': 'Übernommener Mangel nicht gefunden',
        'finding-too-large': 'Zu viele oder zu lange Bildunterschriften',
        'finding-exists': 'Mangel gibt es schon',
        'too-many-findings': 'Höchstzahl an Mängeln für diesen Eintrag erreicht',
        'server-error': 'Server gerade nicht erreichbar',
        other: 'Vom Server abgelehnt',
      },
    },
    notifications: {
      title: 'Benachrichtigungen',
      open: 'Benachrichtigungen',
      openUnread_one: 'Benachrichtigungen, {{count}} ungelesen',
      openUnread_other: 'Benachrichtigungen, {{count}} ungelesen',
      markAllRead: 'Alle gelesen',
      emptyTitle: 'Alles erledigt',
      emptyDescription: 'Neue Benachrichtigungen erscheinen hier.',
      unread: 'Ungelesen',
    },
    profile: {
      open: 'Profil und Einstellungen',
      myProfile: 'Profil und Einstellungen',
      theme: 'Darstellung',
      language: 'Sprache',
      signOutFailed: 'Abmelden fehlgeschlagen. Bitte erneut versuchen.',
    },
    mfaBanner: 'Ohne MFA angemeldet. Verwaltungsrechte sind in dieser Sitzung gesperrt.',
    mfaBannerPassword:
      'Verwaltungsrechte gibt es erst nach einer Anmeldung mit Passkey oder mit Authenticator-App als zweitem Schritt.',
    mfaBannerPasskey:
      'Diese Sitzung ist nur mit Passwort angemeldet, deshalb sind die Verwaltungsrechte gesperrt. Zum Freischalten abmelden und mit dem Passkey neu anmelden.',
    mfaSetUp: 'Einrichten',
    mfaSignInAgain: 'Neu anmelden',
    sessionEnded: 'Sitzung abgelaufen. Bitte neu anmelden.',
  },
  login: {
    title: 'Anmelden',
    chooseCompany: 'Firma wählen',
    chooseCompanyHint: 'Angemeldet wird mit dem Konto der jeweiligen Firma.',
    otherCompany: 'Andere Firma wählen',
    redirecting: 'Weiterleitung zur Anmeldung von {{company}} …',
    noCompanies: 'Keine Firma verfügbar. Bitte die IT kontaktieren.',
    footer: 'Interne Anwendung der STAW-Firmengruppe',
    withSso: 'Mit Firmenkonto anmelden (SSO)',
    withPasskey: 'Mit Passkey anmelden',
    or: 'oder',
    email: 'E-Mail-Adresse',
    emailInvalid: 'Bitte eine vollständige E-Mail-Adresse eingeben.',
    password: 'Passwort',
    signIn: 'Anmelden',
    forgot: 'Passwort vergessen?',
    forgotTitle: 'Passwort vergessen',
    forgotHint:
      'Ein Link zum Festlegen eines neuen Passworts geht an die E-Mail-Adresse des Kontos.',
    sendLink: 'Link senden',
    forgotSent:
      'Gehört die Adresse zu einem Konto mit Passwort, ist eine E-Mail mit Link unterwegs. Der Link gilt eine Stunde.',
    backToLogin: 'Zurück zur Anmeldung',
    passwordSet: 'Das Passwort ist gespeichert. Jetzt damit anmelden.',
    twoFactor: {
      totp: {
        title: 'Code der Authenticator-App',
        hint: 'Die App zeigt für die STAW App alle 30 Sekunden einen neuen Code.',
        code: 'Sechsstelliger Code',
        invalid: 'Die sechs Ziffern aus der App eingeben.',
      },
      backup: {
        title: 'Backup-Code',
        hint: 'Einer der Codes, die beim Einrichten der App gespeichert wurden. Jeder gilt einmal.',
        code: 'Backup-Code',
      },
      useBackup: 'Backup-Code verwenden',
      useApp: 'Code der App verwenden',
      usePasskey: 'Passkey verwenden',
      confirm: 'Bestätigen',
    },
    passkeyStep: {
      title: 'Anmeldung mit Passkey bestätigen',
      hint: 'Für dieses Konto ist ein Passkey als zweiter Schritt eingerichtet. Browser oder Passwort-Manager fragen jetzt danach.',
      confirm: 'Mit Passkey bestätigen',
      unsupported:
        'Dieser Browser kann keine Passkeys verwenden. Bitte einen anderen Browser oder ein anderes Gerät nehmen; sind die Passkeys verloren, hilft die Verwaltung der Firma.',
    },
    errors: {
      'account-locked': 'Dieses Konto ist gesperrt. Bitte die IT der Firma kontaktieren.',
      cancelled: 'Anmeldung abgebrochen.',
      'company-unavailable': 'Diese Firma ist nicht verfügbar.',
      'flow-expired': 'Die Anmeldung hat zu lange gedauert. Bitte erneut anmelden.',
      'mfa-required':
        'Für dieses Konto ist eine Anmeldung mit MFA nötig. Bitte mit MFA erneut anmelden.',
      'provider-error': 'Die Anmeldung bei der Firma ist fehlgeschlagen. Bitte erneut versuchen.',
      'provider-unavailable':
        'Der Anmeldedienst der Firma antwortet nicht. Bitte später erneut versuchen.',
      'rate-limited': 'Zu viele Anmeldeversuche. Bitte eine Minute warten.',
      'sso-unavailable': 'Für diese Firma ist keine Anmeldung per SSO eingerichtet.',
      unknown: 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
    },
  },
  password: {
    new: 'Neues Passwort',
    repeat: 'Neues Passwort wiederholen',
    rules:
      'Mindestens {{min}} Zeichen. Ein Satz aus mehreren Wörtern ist leicht zu merken und schwer zu erraten.',
    tooShort: 'Mindestens {{min}} Zeichen.',
    tooLong: 'Höchstens {{max}} Zeichen.',
    mismatch: 'Die beiden Eingaben stimmen nicht überein.',
    link: {
      invite: {
        title: 'Passwort festlegen',
        hint: 'Damit meldet sich das neue Konto in der STAW App an.',
        submit: 'Passwort festlegen',
        expired:
          'Die Einladung ist abgelaufen oder wurde schon benutzt. Bitte die Verwaltung der Firma um eine neue bitten.',
      },
      reset: {
        title: 'Neues Passwort festlegen',
        hint: 'Danach sind alle Geräte abgemeldet, angemeldet wird mit dem neuen Passwort.',
        submit: 'Passwort speichern',
      },
      incomplete:
        'Der Link ist unvollständig. Bitte den Link aus der E-Mail vollständig öffnen oder in die Adresszeile kopieren.',
      toLogin: 'Zur Anmeldung',
    },
    errors: {
      'account-locked': 'Dieses Konto ist gesperrt. Bitte die Verwaltung der Firma kontaktieren.',
      'company-unavailable': 'Diese Firma ist nicht verfügbar.',
      'invalid-code': 'Der Code stimmt nicht. Bitte den aktuellen Code eingeben.',
      'invalid-credentials': 'E-Mail-Adresse oder Passwort stimmen nicht.',
      'invalid-password': 'Das Passwort stimmt nicht.',
      'invalid-token':
        'Der Link ist abgelaufen oder wurde schon benutzt. Über „Passwort vergessen?“ auf der Anmeldeseite gibt es einen neuen.',
      'mfa-required':
        'Dafür mit Passkey oder mit Passwort und Authenticator-App anmelden. Ist beides verloren, hilft die Verwaltung der Firma.',
      'no-password': 'Für dieses Konto ist kein Passwort festgelegt.',
      'passkey-failed': 'Der Passkey wurde nicht angenommen. Bitte erneut versuchen.',
      'passkey-required':
        'Für dieses Konto ist die Anmeldung mit Passkey als zweiter Schritt nötig.',
      'passkey-unknown':
        'Dieser Passkey ist in der STAW App nicht hinterlegt, vielleicht wurde er entfernt. Bitte mit dem Passwort anmelden.',
      'password-login-disabled': 'Diese Firma meldet sich nur über SSO an.',
      'password-too-long': 'Das Passwort ist zu lang.',
      'password-too-short': 'Das Passwort ist zu kurz.',
      'rate-limited': 'Zu viele Versuche. Bitte später erneut versuchen.',
      'two-factor-expired':
        'Die Anmeldung hat zu lange gedauert. Bitte erneut mit dem Passwort anmelden.',
      'two-factor-locked': 'Zu viele falsche Codes. Die Bestätigung ist für 15 Minuten gesperrt.',
      unknown: 'Fehlgeschlagen. Bitte erneut versuchen, sonst die IT der Firma informieren.',
    },
  },
  home: {
    title: 'Übersicht',
    customize: 'Anpassen',
    customizeTitle: 'Übersicht anpassen',
    customizeDescription:
      'Kacheln ein- und ausblenden und die Reihenfolge ändern. Gilt nur für das eigene Konto.',
    general: 'Allgemein',
    moveUp: '{{title}} nach oben',
    moveDown: '{{title}} nach unten',
    restoreDefault: 'Standard wiederherstellen',
    saveCompanyDefault: 'Als Vorgabe der Firma',
    removeCompanyDefault: 'Vorgabe der Firma entfernen',
    companyDefaultHint:
      'Die Vorgabe der Firma gilt für alle, die ihre Startseite nicht selbst angepasst haben.',
    allHiddenTitle: 'Alle Kacheln sind ausgeblendet',
    allHiddenDescription: 'Über „Anpassen“ lassen sich Kacheln wieder einblenden.',
    tileFailed: 'Kachel konnte nicht geladen werden',
    tiles: {
      reminders: {
        title: 'Meine Wiedervorlagen',
        description: 'Offene Wiedervorlagen, die früheste zuerst',
        emptyDescription: 'Wiedervorlagen entstehen auf der Seite eines Eintrags.',
      },
      approvals: {
        title: 'Meine Genehmigungen',
        description: 'Einträge, die auf eine Entscheidung warten',
        waiting_one: '{{count}} Eintrag wartet',
        waiting_other: '{{count}} Einträge warten',
        waitingMore: 'Mehr als {{count}} Einträge warten',
        emptyTitle: 'Nichts zu entscheiden',
        emptyDescription: 'Neue Anträge erscheinen hier und als Benachrichtigung.',
      },
      recent: {
        title: 'Zuletzt geöffnet',
        description: 'Zuletzt geöffnete Einträge, nur in diesem Browser gespeichert',
        emptyTitle: 'Noch nichts geöffnet',
        emptyDescription:
          'Geöffnete Projekte, Termine, Kunden und andere Einträge erscheinen hier.',
      },
      whatsNew: {
        title: 'Was ist neu',
        description: 'Die neuesten Änderungen der App',
      },
    },
  },
  whatsNew: {
    title: 'Was ist neu',
    description: 'Änderungen in der STAW App, die neuesten zuerst.',
    howTo: 'So geht’s',
    tryIt: 'Ausprobieren',
    emptyTitle: 'Noch keine Neuigkeiten',
    emptyDescription: 'Hier steht künftig, was sich in der App ändert und wie man es nutzt.',
  },
  search: {
    title: 'Suche',
    description: 'Durchsucht alles, was in der aktiven Firma geöffnet werden darf.',
    term: 'Suchbegriff',
    origin: 'Bereich',
    allOrigins: 'Alle Bereiche',
    startTitle: 'Suchbegriff eingeben',
    startDescription: 'Ab zwei Zeichen. Umlaute und kleine Tippfehler sind egal.',
    emptyTitle: 'Nichts gefunden',
    emptyDescription: 'Anderen Begriff oder alle Bereiche versuchen.',
    results_one: '{{count}} Treffer',
    results_other: '{{count}} Treffer',
  },
  files: {
    title: 'Dateien',
    count_one: '{{count}} Datei',
    count_other: '{{count}} Dateien',
    hint: 'Bis {{size}} pro Datei.',
    add: 'Datei hinzufügen',
    empty: 'Noch keine Dateien',
    emptyHint: 'Dateien hierher ziehen oder über „Datei hinzufügen“ auswählen.',
    uploading: 'Wird hochgeladen und geprüft …',
    added: '„{{name}}“ hinzugefügt',
    removed: '„{{name}}“ entfernt',
    remove: '„{{name}}“ entfernen',
    removeTitle: '„{{name}}“ entfernen?',
    removeDescription: 'Die Datei wird endgültig gelöscht.',
    removeConfirm: 'Entfernen',
    removeAll: 'Alle entfernen',
    removeAllTitle_one: '{{count}} Datei entfernen?',
    removeAllTitle_other: 'Alle {{count}} Dateien entfernen?',
    removeAllDescription: 'Die Dateien werden endgültig gelöscht.',
    removeAllConfirm: 'Alle entfernen',
    removedAll_one: '{{count}} Datei entfernt',
    removedAll_other: '{{count}} Dateien entfernt',
    system: 'vom System',
    otherCompany: 'andere Firma',
    unscanned: 'ungeprüft',
    offlineHint: 'Ohne Verbindung: Stand vom letzten Abgleich.',
    offlineUnavailable: 'Ohne Verbindung nicht verfügbar',
    queued: '„{{name}}“ wird hochgeladen, sobald eine Verbindung besteht.',
    waiting: 'Wartet auf den Upload',
    waitingBecause: 'Wartet: {{reason}}',
    uploadRefused: 'Abgelehnt: {{reason}}',
    retryUpload: '„{{name}}“ erneut senden',
    discardUpload: '„{{name}}“ verwerfen',
    expired: 'Abgelaufene Datei',
    notOnDevice: '„{{name}}“ ist nicht auf diesem Gerät gespeichert.',
    downloadZip: 'Alle als ZIP herunterladen',
    errors: {
      'files-zip-too-large':
        'Zusammen sind die Dateien zu groß für ein ZIP-Archiv. Bitte weniger Dateien wählen oder einzeln herunterladen.',
      zipFailed: 'Das ZIP-Archiv konnte nicht heruntergeladen werden. Bitte erneut versuchen.',
      failed: '„{{name}}“ konnte nicht hochgeladen werden.',
      removeFailed: '„{{name}}“ konnte nicht entfernt werden.',
      'file-type-not-allowed': '„{{name}}“: Dieser Dateityp ist hier nicht erlaubt.',
      'file-too-large': '„{{name}}“ ist größer als {{size}}.',
      'file-content-mismatch': '„{{name}}“: Der Inhalt passt nicht zur Dateiendung.',
      'file-infected': '„{{name}}“ enthält Schadsoftware und wurde abgelehnt.',
      'scanner-unavailable':
        'Der Virenscan ist gerade nicht erreichbar, bitte später erneut versuchen.',
      'files-unavailable': 'Der Dateispeicher ist gerade nicht erreichbar.',
      offline: '„{{name}}“: Ohne Verbindung lässt sich hier nichts hochladen.',
      'device-storage-full': 'Auf diesem Gerät ist kein Platz mehr für „{{name}}“.',
      'image-unreadable':
        '„{{name}}“ lässt sich nicht als Bild lesen. Bitte als JPEG oder PNG speichern, bei iPhone-Fotos z. B. unter Einstellungen › Kamera › Formate „Maximale Kompatibilität“ wählen.',
    },
  },
  documentStore: {
    title: 'Dokumente',
    noFolder: 'Noch kein Ordner in SharePoint',
    empty: 'Der Ordner ist leer',
    emptyHint: 'Dateien über „Datei hinzufügen“ hochladen oder direkt in SharePoint ablegen.',
    openFolder: 'In SharePoint öffnen',
    openItem: '„{{name}}“ in SharePoint öffnen',
    link: 'Datei verknüpfen',
    linked: 'verknüpft',
    linkedToast: '„{{name}}“ verknüpft',
    linkTitle: 'SharePoint-Datei verknüpfen',
    linkDescription:
      'Die Datei bleibt, wo sie ist, und erscheint zusätzlich hier. Nur Dateien aus der Bibliothek der Firma.',
    linkUrl: 'Link zur Datei',
    linkInvalid: 'Bitte einen Link aus SharePoint angeben, der mit https:// beginnt.',
    unlink: 'Verknüpfung zu „{{name}}“ entfernen',
    unlinkTitle: 'Verknüpfung zu „{{name}}“ entfernen?',
    unlinkDescription: 'Die Datei bleibt in SharePoint.',
    unlinked: 'Verknüpfung zu „{{name}}“ entfernt',
    removeDescription: 'Die Datei kommt in den Papierkorb der SharePoint-Website.',
    removeAllDescription:
      'Die Dateien kommen in den Papierkorb der SharePoint-Website, verknüpfte Dateien bleiben in SharePoint. Unterordner bleiben.',
    missing: 'Datei nicht mehr in SharePoint',
    errors: {
      'document-conflict':
        'Eine Datei mit diesem Namen wurde gerade geändert. Bitte erneut versuchen.',
      'document-folder-missing': 'Für diesen Eintrag gibt es noch keinen Ordner in SharePoint.',
      'document-not-a-file': 'Der Link führt zu einem Ordner, nicht zu einer Datei.',
      'document-not-found': 'Die Datei ist nicht mehr in SharePoint.',
      'document-outside-library':
        'Die Datei liegt nicht in der SharePoint-Bibliothek der Firma und lässt sich nicht verknüpfen.',
      'sharepoint-denied':
        'SharePoint hat den Zugriff abgelehnt. Die Verwaltung muss die Anbindung prüfen.',
      'sharepoint-disabled': 'Die Ablage in SharePoint ist ausgeschaltet.',
      'sharepoint-unavailable': 'SharePoint ist gerade nicht erreichbar. Später erneut versuchen.',
    },
  },
  customFields: {
    title: 'Weitere Angaben',
    none: 'Keine Auswahl',
    yes: 'Ja',
    no: 'Nein',
    conflict: 'Die Angaben wurden inzwischen geändert. Die aktuelle Fassung ist geladen.',
    errors: {
      required: 'Pflichtfeld',
      invalid: 'Ungültiger Wert',
      'too-long': 'Zu lang',
      'unknown-option': 'Diese Auswahl gibt es nicht mehr',
    },
  },
  ai: { generated: 'Von KI erstellt' },
  sharing: {
    title: 'Freigaben',
    description: 'Andere Firmen, die diesen Eintrag lesen dürfen.',
    share: 'Freigeben',
    none: 'Nur diese Firma sieht den Eintrag.',
    until: 'bis {{date}}',
    unlimited: 'unbefristet',
    from: 'Freigegeben von {{company}}',
    readOnly: 'nur lesen',
    end: 'Freigabe für {{company}} beenden',
    endTitle: 'Freigabe für {{company}} beenden?',
    endDescription: 'Die Firma sieht den Eintrag sofort nicht mehr.',
    endConfirm: 'Beenden',
    ended: 'Freigabe für {{company}} beendet',
    shared: 'Für {{company}} freigegeben',
    dialogTitle: 'Eintrag freigeben',
    dialogDescription: 'Eine andere Firma darf den Eintrag lesen, aber nicht ändern.',
    company: 'Firma',
    chooseCompany: 'Firma wählen',
    noCompanies: 'Keine weitere Firma nutzt dieses Modul.',
    alsoShare: 'Zusätzlich freigeben',
    expiry: 'Freigabe endet am',
    expiryHint: 'Ohne Datum gilt die Freigabe, bis sie jemand beendet.',
    preview: 'Die Firma sieht',
    previewRecord: 'den Eintrag mit allen Angaben',
    gdprHint:
      'Enthält der Eintrag personenbezogene Daten, braucht es eine Vereinbarung zwischen den Firmen. Im Zweifel den Datenschutz fragen.',
    badge: 'von {{company}}',
    badgeUnknown: 'freigegeben',
    parts: {
      files: 'Dateien',
      activity: 'Kommentare',
      customFields: 'Weitere Angaben',
      workflows: 'Status',
    },
    errors: { exists: 'Die Firma sieht den Eintrag bereits.' },
  },
  workflow: {
    title: 'Status',
    moved: 'Neuer Status: {{state}}',
    reason: 'Begründung',
    reasonHint: 'Sichtbar für alle, die den Eintrag sehen.',
    otherCompany: 'andere Firma',
    system: 'System',
    errors: {
      ownRequest: 'Über eine eigene Anfrage entscheidet eine andere Person.',
      changed: 'Der Status wurde inzwischen geändert. Die aktuelle Fassung ist geladen.',
    },
  },
  forMe: {
    title: 'Für mich',
    description:
      'Genehmigungen, Aufgaben und Wiedervorlagen aus allen Modulen. Erledigt wird auf der Seite des Eintrags.',
    now_one: '{{count}} Eintrag braucht jetzt eine Handlung',
    now_other: '{{count}} Einträge brauchen jetzt eine Handlung',
    nothingNow: 'Gerade wartet nichts',
    approvals: 'Zu entscheiden',
    tasks: 'Meine Aufgaben',
    due: 'Fällige Wiedervorlagen',
    coming: 'Anstehende Wiedervorlagen',
    emptyTitle: 'Alles erledigt',
    emptyDescription:
      'Genehmigungen, Aufgaben und Wiedervorlagen erscheinen hier, sobald sie anstehen. Aufgaben und Wiedervorlagen entstehen auf der Seite eines Eintrags unter „Verlauf“.',
  },
  approvals: {
    untitled: 'Eintrag ohne Titel',
    requestedBy: 'von {{name}}',
    more: 'Es warten weitere Anfragen. Sichtbar sind die ältesten 100.',
  },
  reminders: {
    title: 'Wiedervorlagen',
    add: 'Wiedervorlage',
    none: 'Keine Wiedervorlage für diesen Eintrag.',
    addTitle: 'Wiedervorlage anlegen',
    editTitle: 'Wiedervorlage ändern',
    dialogDescription:
      'Am gewählten Tag kommt eine Benachrichtigung. Wiedervorlagen sieht nur, wer sie angelegt hat.',
    dueOn: 'Fällig am',
    dueOnInvalid: 'Bitte ein Datum wählen.',
    quick: { week: 'In 1 Woche', month: 'In 1 Monat', quarter: 'In 3 Monaten' },
    note: 'Notiz',
    noteHint: 'Kurz, worum es geht, z. B. „Angebot nachfassen“.',
    noteTooLong: 'Höchstens {{max}} Zeichen.',
    saved: 'Wiedervorlage gespeichert',
    finish: 'Wiedervorlage vom {{day}} erledigt',
    reopen: 'Wiedervorlage vom {{day}} wieder öffnen',
    overdue: 'Überfällig',
    edit: 'Wiedervorlage ändern',
    remove: 'Wiedervorlage löschen',
    removeTitle: 'Wiedervorlage löschen?',
    removeDescription: 'Die Wiedervorlage und ihre Notiz werden entfernt.',
    removeConfirm: 'Löschen',
    emptyTitle: 'Keine offenen Wiedervorlagen',
    untitled: 'Eintrag ohne Titel',
    more: 'Es gibt weitere Wiedervorlagen. Sichtbar sind die frühesten 100.',
    errors: {
      conflict: 'Die Wiedervorlage wurde inzwischen geändert. Bitte neu laden.',
      tooMany: 'Zu viele offene Wiedervorlagen. Bitte zuerst erledigte abhaken.',
    },
  },
  tasks: {
    title: 'Aufgaben',
    add: 'Aufgabe',
    none: 'Keine offene Aufgabe für diesen Eintrag.',
    addTitle: 'Aufgabe anlegen',
    editTitle: 'Aufgabe ändern',
    dialogDescription:
      'Die zuständige Person bekommt eine Benachrichtigung, am fälligen Tag eine weitere. Aufgaben sieht jeder, der hier Aufgaben bearbeiten darf.',
    fields: {
      title: 'Was ist zu tun?',
      description: 'Beschreibung',
      assignee: 'Zuständig',
      dueOn: 'Fällig am',
    },
    titleMissing: 'Bitte kurz beschreiben, was zu tun ist.',
    titleTooLong: 'Höchstens {{max}} Zeichen.',
    descriptionTooLong: 'Höchstens {{max}} Zeichen.',
    assigneeMissing: 'Bitte eine Person wählen.',
    saved: 'Aufgabe gespeichert',
    finish: '„{{title}}“ erledigt',
    reopen: '„{{title}}“ wieder öffnen',
    overdue: 'Überfällig',
    due: 'fällig {{day}}',
    edit: 'Aufgabe ändern',
    remove: 'Aufgabe löschen',
    removeTitle: 'Aufgabe löschen?',
    removeDescription: 'Die Aufgabe und ihre Beschreibung werden entfernt.',
    removeConfirm: 'Löschen',
    untitled: 'Eintrag ohne Titel',
    more: 'Es gibt weitere Aufgaben. Sichtbar sind die frühesten 100.',
    errors: {
      assignee: 'Die Person kann diesen Eintrag oder seine Aufgaben nicht sehen.',
      conflict: 'Die Aufgabe wurde inzwischen geändert. Bitte neu laden.',
      tooMany: 'Zu viele offene Aufgaben an diesem Eintrag. Bitte zuerst erledigte abhaken.',
    },
  },
  recordSections: {
    failed: 'Abschnitt konnte nicht geladen werden',
    label: 'Abschnitte',
    overview: 'Übersicht',
    files: 'Dateien',
    history: 'Verlauf',
  },
  activity: {
    title: 'Verlauf',
    description: 'Kommentare und Änderungen, neueste zuerst.',
    placeholder: 'Kommentar schreiben …',
    label: 'Kommentar',
    send: 'Senden',
    sendHint: 'Strg+Enter sendet, @ erwähnt eine Person.',
    mentionList: 'Personen zum Erwähnen',
    empty: 'Noch keine Einträge',
    emptyHint: 'Kommentare und Änderungen erscheinen hier.',
    more: 'Ältere laden',
    edited: 'bearbeitet',
    edit: 'Kommentar bearbeiten',
    save: 'Speichern',
    cancel: 'Abbrechen',
    remove: 'Kommentar löschen',
    removeTitle: 'Kommentar löschen?',
    removeDescription: 'Der Text wird endgültig entfernt.',
    removeConfirm: 'Löschen',
    system: 'System',
    otherCompany: 'andere Firma',
    platform: 'Plattform-Administration',
    unknownAction: 'Änderung ({{action}})',
    offline: 'offline erfasst',
    offlineNote: 'Ohne Verbindung zeigt der Verlauf nur die Kommentare auf diesem Gerät.',
    pending: 'wartet auf Abgleich',
    unavailable: 'Ohne Verbindung nicht verfügbar.',
    tooLong: 'Höchstens {{max}} Zeichen.',
    errors: {
      sendFailed: 'Kommentar konnte nicht gesendet werden.',
      saveFailed: 'Änderung konnte nicht gespeichert werden.',
      conflict: 'Der Kommentar wurde inzwischen geändert. Bitte neu laden.',
      removeFailed: 'Kommentar konnte nicht gelöscht werden.',
    },
  },
  geo: {
    placeholder: 'Adresse suchen',
    searchPlaceholder: 'Straße, Ort oder Firma eingeben',
    typeMore: 'Mindestens zwei Zeichen eingeben',
    noResults: 'Keine Adresse gefunden',
    failed: 'Adresssuche gerade nicht erreichbar',
    unavailable: 'Adresssuche nicht eingerichtet',
    mapUnavailable: 'Karte nicht eingerichtet',
    attribution: '© OpenStreetMap-Mitwirkende',
    picker: {
      title: 'Punkt auf der Karte',
      hint: 'Für Orte ohne passende Adresse, z. B. eine Baustelle: Punkt auf der Karte wählen, Koordinaten eingeben oder den eigenen Standort nehmen.',
      map: 'Karte zum Festlegen des Punkts',
      marker: 'Gewählter Punkt, zum Verschieben ziehen',
      coordinates: 'Koordinaten',
      coordinatesHint: 'Breite, Länge, z. B. aus Google Maps oder einer GPS-App',
      invalid: 'Bitte Breite und Länge eingeben, z. B. 52.520008, 13.404954.',
      openMap: 'Auf der Karte wählen',
      closeMap: 'Karte schließen',
      mapHint: 'Auf die Karte tippen oder die Markierung ziehen.',
      locate: 'Mein Standort',
      locateDenied:
        'Der Browser darf den Standort nicht abfragen. Bitte in den Einstellungen erlauben.',
      locateFailed: 'Der Standort ließ sich gerade nicht bestimmen.',
      clear: 'Punkt entfernen',
    },
    controls: {
      marker: 'Markierung',
      zoomIn: 'Vergrößern',
      zoomOut: 'Verkleinern',
      attribution: 'Quellenangabe zeigen',
      windowsHelp: 'Mit Strg und Mausrad zoomen',
      macHelp: 'Mit ⌘ und Mausrad zoomen',
      mobileHelp: 'Mit zwei Fingern verschieben',
    },
  },
  vat: {
    check: 'USt-IdNr. prüfen',
    valid: 'Gültig',
    invalid: 'Nicht vergeben',
    unavailable: 'VIES gerade nicht erreichbar',
    failed: 'Prüfung fehlgeschlagen',
    rateLimited: 'Zu viele Prüfungen, bitte gleich noch einmal',
    checkedAt: 'geprüft {{when}}',
    problems: {
      empty: 'Keine USt-IdNr. eingegeben',
      noCountry: 'Ländercode fehlt, z. B. DE',
      notEu: 'Keine USt-IdNr. aus der EU',
      format: 'Aufbau passt nicht zum Land',
    },
  },
  imports: {
    open: 'Importieren',
    title: '{{what}} importieren',
    description: 'Aus einer CSV-Datei, z. B. aus Excel über „Speichern unter › CSV“.',
    steps: { file: 'Datei', columns: 'Spalten', check: 'Prüfen' },
    fileHint:
      'Die erste Zeile enthält die Spaltennamen. Die Datei bleibt auf dem Gerät, übertragen werden nur die zugeordneten Werte.',
    chooseFile: 'CSV-Datei wählen',
    fileErrors: {
      empty: 'Die Datei enthält keine Zeilen unter den Spaltennamen.',
      'too-many-rows': 'Die Datei hat mehr als 10.000 Zeilen. Bitte aufteilen.',
      'too-many-columns': 'Die Datei hat mehr als 60 Spalten.',
      'unclosed-quote': 'Die Datei ist beschädigt: Ein Anführungszeichen wird nicht geschlossen.',
      unreadable: 'Die Datei lässt sich nicht lesen.',
    },
    columnsHint_one: '{{file}}: {{count}} Zeile. Jedem Feld eine Spalte zuordnen.',
    columnsHint_other: '{{file}}: {{count}} Zeilen. Jedem Feld eine Spalte zuordnen.',
    notMapped: 'Nicht importieren',
    column: 'Spalte {{number}}',
    example: 'Erste Zeile: {{value}}',
    checkRows: 'Zeilen prüfen',
    checking: 'Prüfe {{done}} von {{total}} Zeilen …',
    importing: 'Importiere {{done}} von {{total}} Zeilen …',
    counts: { new: 'Neu', duplicate: 'Schon vorhanden', invalid: 'Werden übersprungen' },
    duplicates: 'Vorhandene Einträge',
    duplicatesHint:
      'Aktualisieren überschreibt die zugeordneten Felder mit den Werten aus der Datei.',
    skip: 'Überspringen',
    update: 'Aktualisieren',
    attention: 'Zeilen mit Hinweisen',
    line: 'Zeile',
    status: 'Status',
    details: 'Hinweis',
    statuses: {
      invalid: 'Fehlerhaft',
      duplicate: 'Vorhanden',
      repeat: 'Doppelt in der Datei',
      new: 'Neu',
    },
    repeatOf: 'Wie Zeile {{line}}',
    more_one: 'Und {{count}} weitere Zeile.',
    more_other: 'Und {{count}} weitere Zeilen.',
    row: 'Zeile',
    errors: {
      required: 'fehlt',
      too_long: 'zu lang',
      too_short: 'zu kurz',
      invalid: 'ungültig',
    },
    run_one: '{{count}} Zeile importieren',
    run_other: '{{count}} Zeilen importieren',
    failed: 'Der Import wurde abgebrochen: {{message}}',
    unavailable: 'Der Import ist gerade nicht möglich. Bitte später erneut versuchen.',
    partial_one: 'Bis dahin wurde {{count}} Zeile importiert.',
    partial_other: 'Bis dahin wurden {{count}} Zeilen importiert.',
    doneTitle: 'Import abgeschlossen',
    done: '{{created}} neu angelegt, {{updated}} aktualisiert, {{skipped}} übersprungen.',
    close: 'Fertig',
  },
  exports: {
    csv: 'Exportieren',
    xlsxFile: 'Excel-Datei (.xlsx)',
    csvFile: 'CSV-Datei (.csv)',
    vcard: 'Kontakt speichern',
  },
  views: {
    trigger: 'Ansichten',
    current: 'Ansicht: {{name}}',
    personal: 'Meine Ansichten',
    shared: 'Für die ganze Firma',
    none: 'Noch keine Ansicht gespeichert',
    save: 'Aktuelle Filter speichern …',
    manage: 'Ansichten verwalten …',
    saveTitle: 'Ansicht speichern',
    saveDescription: 'Speichert Suche und Filter dieser Liste unter einem Namen.',
    name: 'Name',
    share: 'Für die ganze Firma speichern',
    shareHint: 'Alle, die diese Liste sehen, finden die Ansicht in ihrer Auswahl.',
    saved: 'Ansicht gespeichert',
    manageTitle: 'Ansichten verwalten',
    remove: '{{name}} löschen',
    removeTitle: 'Ansicht löschen?',
    removeDescription: '„{{name}}“ wird gelöscht. Die Daten der Liste bleiben unverändert.',
    removeSharedDescription:
      '„{{name}}“ wird für die ganze Firma gelöscht. Die Daten der Liste bleiben unverändert.',
    errors: {
      nameTaken: 'Eine Ansicht mit diesem Namen gibt es schon.',
      tooMany: 'Höchstens {{max}} eigene Ansichten pro Liste. Bitte eine alte löschen.',
      removeFailed: 'Ansicht konnte nicht gelöscht werden.',
    },
  },
  help: {
    title: 'Anleitungen',
    description: 'Wie die STAW App und ihre Module funktionieren.',
    term: 'Anleitungen durchsuchen',
    area: 'Bereich',
    allAreas: 'Alle Bereiche',
    areas: { basics: 'Grundlagen', admin: 'Verwaltung', development: 'Entwicklung' },
    button: 'Anleitung',
    onThisPage: 'Auf dieser Seite',
    results_one: '{{count}} Treffer',
    results_other: '{{count}} Treffer',
    emptyTitle: 'Nichts gefunden',
    emptyDescription: 'Anderen Begriff oder alle Bereiche versuchen.',
    noneTitle: 'Keine Anleitungen',
    noneDescription: 'Für diesen Bereich gibt es noch keine Anleitungen.',
    api: {
      title: 'API-Referenz',
      description:
        'Alle Schnittstellen des Kerns und der aktiven Module, mit Rechten und Eingaben.',
      download: 'OpenAPI herunterladen',
      filter: 'Pfad, Beschreibung oder Berechtigung',
      empty: 'Anderen Begriff oder alle Bereiche versuchen.',
      kernel: 'Kern',
      count_one: '{{count}} Schnittstelle',
      count_other: '{{count}} Schnittstellen',
      intro:
        'Alle Pfade liegen unter `/api`. Aufrufe laufen mit der Sitzung der App, also nach der Anmeldung im Browser. Schreibende Aufrufe brauchen zusätzlich den CSRF-Header; Name und Werte stehen in der OpenAPI-Datei. Mit einem `Idempotency-Key` (UUID) wirkt ein wiederholter Aufruf nur einmal. Die Liste zeigt die Schnittstellen der aktiven Firma; aufrufen lässt sich nur, wofür die Berechtigung vorhanden ist.',
      public: 'Ohne Anmeldung erreichbar.',
      authenticated: 'Braucht eine Anmeldung, keine Berechtigung.',
      permission: 'Braucht die Berechtigung {{permission}}.',
      scope: 'Umfang mindestens {{scope}}.',
      parameters: 'Parameter',
      body: 'Inhalt (JSON)',
      responses: 'Antworten',
      yes: 'ja',
      no: 'nein',
      columns: {
        name: 'Name',
        in: 'Ort',
        type: 'Typ',
        required: 'Pflicht',
        rules: 'Regeln',
        field: 'Feld',
      },
      in: { path: 'Pfad', query: 'Query', header: 'Header' },
      rules: { length: 'Länge', range: 'Wert', items: 'Anzahl', default: 'Standard' },
      status: {
        '200': 'OK',
        '201': 'Angelegt',
        '204': 'Erledigt, ohne Inhalt',
        '400': 'Eingaben ungültig (`invalid-input`)',
        '401': 'Nicht angemeldet',
        '403': 'Berechtigung fehlt',
        '404': 'Nicht gefunden',
        '409': 'Konflikt, z. B. veraltete Version',
        '429': 'Zu viele Anfragen, `Retry-After` beachten',
      },
    },
    guides: {
      start: {
        title: 'Erste Schritte',
        summary: 'Anmelden, Firma wechseln, Navigation, Übersicht anpassen und Startseite.',
      },
      search: {
        title: 'Suchen',
        summary: 'Personen, Datensätze, Seiten und Anleitungen mit Strg K finden.',
      },
      files: {
        title: 'Dateien anhängen',
        summary: 'Hochladen, erlaubte Dateiarten, Virenscan, Herunterladen und Entfernen.',
      },
      activity: {
        title: 'Kommentare und Verlauf',
        summary:
          'Kommentieren, Benachrichtigungen bei Antworten und die Änderungen eines Eintrags.',
      },
      forMe: {
        title: 'Für mich',
        summary:
          'Genehmigungen, Aufgaben und Wiedervorlagen an einer Stelle, die Zahl in der Navigation.',
      },
      workflows: {
        title: 'Status und Genehmigungen',
        summary: 'Schritte eines Eintrags, Begründungen und Genehmigungen.',
      },
      sharing: {
        title: 'Freigaben zwischen Firmen',
        summary: 'Einträge anderen Firmen zum Lesen freigeben und Freigaben beenden.',
      },
      offline: {
        title: 'Offline arbeiten',
        summary:
          'Daten ohne Verbindung nutzen, Synchronisierung, Konflikte und Datenschutz auf dem Gerät.',
      },
      ai: {
        title: 'KI-Funktionen',
        summary: 'Anbieter und Schlüssel einrichten, Datenstufe wählen, Funktionen und Verbrauch.',
      },
      privacy: {
        title: 'Datenschutz: Auskunft, Löschung, Fristen',
        summary: 'Daten einer Person exportieren, Personen löschen und Löschfristen einschalten.',
      },
      profile: {
        title: 'Profil und Benachrichtigungen',
        summary:
          'Sprache, Darstellung, Passwort und Authenticator-App, Unterschrift, Hinweise auf neue Benachrichtigungen.',
      },
      accounts: {
        title: 'Konten und Gruppen',
        summary: 'Wie Konten entstehen, eingeladen und gesperrt werden und in Gruppen kommen.',
      },
      roles: {
        title: 'Rollen und Rechte',
        summary: 'Berechtigungen bündeln, zuweisen, befristen und regelmäßig prüfen.',
      },
      sso: {
        title: 'Anmeldung einrichten',
        summary:
          'SSO per OpenID Connect oder SAML 2.0 anbinden und testen, Anmeldung mit Passwort erlauben oder beenden.',
      },
      integrations: {
        title: 'SharePoint anbinden',
        summary:
          'Dokumente der Module in einer SharePoint-Bibliothek der Firma ablegen: App-Registrierung, Freigabe der Website, Verbindung testen.',
      },
      notifications: {
        title: 'Benachrichtigungskanäle einrichten',
        summary:
          'Eigenen Mailserver, Microsoft Teams und Push-Nachrichten für die Firma einrichten.',
      },
      customFields: {
        title: 'Zusatzfelder einrichten',
        summary: 'Eigene Felder für Einträge der Module anlegen, sortieren und löschen.',
      },
      company: {
        title: 'Vorgaben und Erscheinungsbild',
        summary: 'Einstellungen für alle Personen der Firma, Logo und Firmenfarbe.',
      },
      audit: {
        title: 'Audit-Log',
        summary: 'Nachvollziehen, wer wann was geändert hat.',
      },
      platform: {
        title: 'Firmen, Module und Plattformrechte',
        summary: 'Für Superadmins: Firmen anlegen, Module schalten, Rechte über alle Firmen.',
      },
      operations: {
        title: 'Betrieb und Fehler-IDs',
        summary: 'Zustand von Server, Datenbank und Jobs ablesen, Fehler-IDs im Log finden.',
      },
    },
  },
  profile: {
    title: 'Profil und Einstellungen',
    description: 'Gilt für dieses Konto in allen Browsern und Geräten.',
    account: 'Konto',
    name: 'Name',
    email: 'E-Mail',
    company: 'Firma',
    signInMethod: 'Anmeldung',
    methods: { oidc: 'SSO', saml: 'SSO', password: 'E-Mail und Passwort', passkey: 'Passkey' },
    mfa: 'MFA',
    mfaYes: 'Mit MFA angemeldet',
    mfaNo: 'Ohne MFA angemeldet',
    security: {
      title: 'Anmeldung',
      password: 'Passwort',
      passwordHint: 'Anmeldung mit {{email}}.',
      change: 'Passwort ändern',
      changeHint: 'Andere Geräte werden danach abgemeldet.',
      current: 'Aktuelles Passwort',
      save: 'Passwort speichern',
      changed: 'Passwort geändert',
      app: 'Authenticator-App',
      appOff:
        'Zweiter Schritt der Anmeldung mit einem Code aus einer App wie Microsoft Authenticator. Für Verwaltungsrechte nötig.',
      appOn: 'Jede Anmeldung fragt nach einem Code der App.',
      on: 'Eingerichtet',
      setUp: 'Einrichten',
      setupTitle: 'Authenticator-App einrichten',
      confirmPassword: 'Zur Bestätigung das aktuelle Passwort eingeben.',
      continue: 'Weiter',
      scan: 'Den QR-Code mit der App scannen oder den Schlüssel von Hand eingeben, dann den Code der App eintragen.',
      qrLabel: 'QR-Code für die Authenticator-App',
      openApp: 'In der App öffnen',
      secret: 'Schlüssel',
      copySecret: 'Schlüssel kopieren',
      code: 'Code aus der App',
      confirm: 'Bestätigen',
      enabled: 'Authenticator-App eingerichtet',
      codesTitle: 'Backup-Codes',
      codesHint:
        'Jeder Code ersetzt einmal den Code der App, z. B. wenn das Handy fehlt. Jetzt sicher aufbewahren, sie werden nicht wieder angezeigt.',
      codesFile: 'Backup-Codes der STAW App, {{company}}',
      copy: 'Kopieren',
      copied: 'Kopiert',
      download: 'Herunterladen',
      done: 'Fertig',
      newCodes: 'Neue Backup-Codes',
      newCodesConfirm:
        'Die bisherigen Codes werden ungültig. Zur Bestätigung das Passwort eingeben.',
      newCodesHint: 'Die bisherigen Codes gelten nicht mehr. Die neuen jetzt sicher aufbewahren.',
      generate: 'Erzeugen',
      remove: 'Entfernen',
      removeTitle: 'Authenticator-App entfernen?',
      removeHint:
        'Danach reicht das Passwort für die Anmeldung, Verwaltungsrechte gibt es erst wieder mit der App. Andere Geräte werden abgemeldet.',
      removed: 'Authenticator-App entfernt',
      passkeys: 'Passkeys',
      passkeysHint:
        'Anmeldung ohne Passwort, bestätigt mit Fingerabdruck, Gesicht, PIN oder einem Passwort-Manager wie 1Password. Gilt als MFA.',
      needsMfa:
        'Ändern geht nach einer Anmeldung mit Passkey oder Authenticator-App. Ist beides verloren, setzt die Verwaltung den zweiten Faktor zurück.',
      passkeysUnsupported: 'Dieser Browser unterstützt keine Passkeys.',
      addPasskey: 'Passkey hinzufügen',
      addPasskeyHint:
        'Zur Bestätigung das aktuelle Passwort eingeben. Danach fragt der Browser oder der Passwort-Manager, wo der Passkey gespeichert wird.',
      passkeyAdded: 'Passkey hinzugefügt',
      passkeyKnown: 'Für dieses Konto liegt dort schon ein Passkey.',
      passkeyName: 'Passkey',
      synced: 'Synchronisiert',
      passkeyCreated: 'Hinzugefügt am {{date}}',
      passkeyUsed: 'zuletzt verwendet am {{date}}',
      passkeyNeverUsed: 'noch nicht verwendet',
      removePasskey: '{{name}} entfernen',
      removePasskeyTitle: 'Passkey „{{name}}“ entfernen?',
      removePasskeyHint:
        'Mit diesem Passkey ist danach keine Anmeldung mehr möglich, andere Geräte werden abgemeldet. Im Passwort-Manager bitte ebenfalls löschen.',
      passkeyRemoved: 'Passkey entfernt',
    },
    sections: {
      general: 'Allgemein',
      appearance: 'Darstellung',
      notifications: 'Benachrichtigungen',
      accessibility: 'Barrierefreiheit',
    },
    changed: 'Angepasst',
    resetToDefault: 'Auf Vorgabe zurücksetzen',
    channels: {
      always: 'Immer',
      app: {
        label: 'In der App',
        description:
          'Alle Benachrichtigungen erscheinen in der App. Die Kanäle unten weisen nur darauf hin, ohne Inhalt.',
        onlyChannel:
          'Alle Benachrichtigungen erscheinen in der App. Weitere Kanäle hat die Firma nicht eingerichtet.',
      },
      email: {
        description: 'Hinweis an {{address}}, wenn ungelesene Benachrichtigungen warten.',
        noAddress: 'Für dieses Konto ist keine E-Mail-Adresse bekannt.',
      },
      push: {
        label: 'Push-Nachrichten',
        description:
          'Hinweis in der Mitteilungszentrale dieses Geräts, wenn ungelesene Benachrichtigungen warten.',
        active: 'Auf diesem Gerät eingeschaltet. Hinweise erscheinen in der Mitteilungszentrale.',
        install:
          'Auf iPhone und iPad nur in der installierten App: in Safari über Teilen › Zum Home-Bildschirm hinzufügen, die STAW App vom Home-Bildschirm öffnen und hier einschalten.',
        unsupported: 'Dieser Browser kann keine Push-Nachrichten empfangen.',
        blocked:
          'Mitteilungen sind für die STAW App blockiert. Auf iPhone und iPad unter Einstellungen › Mitteilungen › STAW App erlauben, am Computer in den Website-Einstellungen des Browsers.',
        on: 'Aktiv',
        switchOn: 'Auf diesem Gerät einschalten',
        switchOff: 'Ausschalten',
        switchedOn: 'Push-Nachrichten eingeschaltet',
        switchedOff: 'Push-Nachrichten ausgeschaltet',
        test: 'Test senden',
        testSent: 'Test gesendet. Er erscheint in wenigen Sekunden.',
        since: 'Eingeschaltet seit {{date}}',
        devices: {
          iphone: 'iPhone',
          ipad: 'iPad',
          mac: 'Mac',
          windows: 'Windows-PC',
          android: 'Android-Gerät',
          linux: 'Linux-PC',
          other: 'Anderes Gerät',
        },
        errors: {
          denied:
            'Mitteilungen wurden abgelehnt. Sie lassen sich in den Einstellungen des Geräts bzw. Browsers wieder erlauben.',
          'no-worker': 'Die App ist noch nicht vollständig geladen. Bitte die Seite neu laden.',
          'no-device': 'Auf keinem Gerät sind Push-Nachrichten eingeschaltet.',
          'too-many-devices': 'Zu viele Geräte. Bitte zuerst ein altes Gerät ausschalten.',
          unavailable: 'Push-Nachrichten sind für die Firma ausgeschaltet.',
          rejected: 'Der Push-Dienst des Geräts hat die Nachricht abgelehnt.',
          'connection-failed':
            'Der Push-Dienst ist gerade nicht erreichbar. Bitte später erneut versuchen.',
        },
      },
    },
    moduleSettings: 'Einstellungen der Module',
    moduleSettingsHint: 'Weitere persönliche Einstellungen liegen bei den Modulen.',
  },
  moduleSettings: {
    title: 'Einstellungen',
    description: 'Persönliche Einstellungen und, mit dem Recht dazu, die Vorgaben der Firma.',
    personal: 'Meine Einstellungen',
    company: 'Für die Firma',
    personalDefaults: 'Vorgaben für persönliche Einstellungen',
    global: 'Für alle Firmen',
    empty: 'Hier gibt es nichts einzustellen.',
  },
  sequences: {
    title: 'Fortlaufende Nummern',
    description:
      'Nummern, die neue Datensätze in dieser Firma bekommen. Bereits vergebene Nummern bleiben, wie sie sind.',
    mode: 'Vergabe',
    modes: {
      auto: {
        label: 'Automatisch',
        description: 'Die STAW App vergibt die Nummer nach dem Format.',
      },
      manual: {
        label: 'Von Hand',
        description:
          'Wer einen Datensatz anlegt, gibt die Nummer selbst ein. Doppelte lehnt die App ab.',
      },
    },
    format: 'Format',
    formatHelp:
      '{n} ist die Nummer, {n:5} füllt sie mit Nullen auf fünf Stellen auf, {year} ist das Jahr und startet die Zählung jedes Jahr neu, {rand:6} sind sechs Zufallsziffern (4 bis 12).',
    formatInvalid:
      'Das Format braucht {n} oder {rand:4} bis {rand:12}, jedes höchstens einmal, und darf sonst nur Buchstaben, Ziffern, Leerzeichen und . _ / # - enthalten.',
    next: 'Nächste Nummer',
    nextInvalid: 'Bitte eine ganze Zahl ab 1 eingeben.',
    nextLower: 'Darf nicht kleiner sein als {{current}}, sonst kämen Nummern doppelt vor.',
    preview: 'Der nächste Datensatz bekommt {{preview}}.',
    previewRandom: 'Der nächste Datensatz bekommt zum Beispiel {{preview}}.',
    previewManual: 'Die Nummer wird beim Anlegen eingegeben.',
    number: 'Nummer',
    numberRequired: 'Bitte eine Nummer eingeben.',
    numberInvalid:
      'Höchstens 40 Zeichen: Buchstaben, Ziffern, Leerzeichen und . _ / # -, mindestens ein Buchstabe oder eine Ziffer.',
    numberTaken: 'Diese Nummer ist in der Firma schon vergeben. Bitte eine andere eingeben.',
    exhausted:
      'Es war keine freie Nummer zu finden. Die Verwaltung kann im Format mehr Stellen oder eine höhere nächste Nummer einstellen.',
    changed: 'Angepasst',
    save: 'Speichern',
    saved: 'Nummernkreis gespeichert',
  },
  errors: {
    notFoundTitle: 'Seite nicht gefunden',
    notFoundDescription: 'Die Adresse ist falsch oder die Seite wurde verschoben.',
    forbiddenTitle: 'Kein Zugriff',
    forbiddenDescription:
      'Für diese Seite fehlt eine Berechtigung. Die Verwaltung der Firma kann sie vergeben.',
    crashTitle: 'Etwas ist schiefgelaufen',
    crashDescription: 'Die Seite konnte nicht angezeigt werden. Neu laden hilft oft.',
    reload: 'Neu laden',
    toHome: 'Zur Startseite',
    serverError:
      'Fehler auf dem Server. Bitte erneut versuchen oder dem Support die Fehler-ID nennen.',
    errorId: 'Fehler-ID: {{id}}',
    crashErrorId: 'Für den Support die Fehler-ID:',
    copyErrorId: 'Fehler-ID kopieren',
    errorIdCopied: 'Fehler-ID kopiert',
  },
  core: {
    roles: { people: 'Personenverzeichnis' },
    permissions: {
      people: {
        read: {
          label: 'Personen der Firma sehen',
          description:
            'Name und dienstliche E-Mail der Kolleginnen und Kollegen, z. B. in Auswahlfeldern.',
        },
      },
    },
    people: {
      placeholder: 'Person auswählen',
      search: 'Name eingeben',
      empty: 'Keine Person gefunden',
      tooShort: 'Mindestens zwei Buchstaben eingeben',
      offline: 'Ohne Verbindung nicht auf diesem Gerät',
      loading: 'Wird geladen …',
      unavailable: 'nicht verfügbar',
      locked: 'gesperrt',
      add: 'Person hinzufügen',
      remove: '{{name}} entfernen',
    },
    ...NOTIFICATION_TITLES.de,
    settings: {
      locale: {
        label: 'Sprache',
        description: 'Sprache der Oberfläche.',
        options: { de: 'Deutsch', en: 'English' },
      },
      startPage: {
        label: 'Startseite',
        description: 'Seite, die nach der Anmeldung öffnet.',
      },
      dashboard: {
        label: 'Kacheln der Übersicht',
        description: 'Reihenfolge und ausgeblendete Kacheln, festgelegt auf der Übersicht.',
      },
      theme: {
        label: 'Farbschema',
        description: 'Hell, dunkel oder wie im Betriebssystem eingestellt.',
        options: { system: 'System', light: 'Hell', dark: 'Dunkel' },
      },
      density: {
        label: 'Dichte',
        description: 'Kompakt zeigt mehr Zeilen in Listen und Tabellen.',
        options: { comfortable: 'Normal', compact: 'Kompakt' },
      },
      emailNotifications: {
        label: 'E-Mail',
        description: 'Hinweis per E-Mail, wenn ungelesene Benachrichtigungen warten.',
      },
      calendarInvites: {
        label: 'Kalendereinladungen',
        description:
          'Termine, für die man eingetragen ist, kommen als Einladung per E-Mail und landen so im Outlook-Kalender. Änderungen und Absagen kommen nach.',
      },
      teamsNotifications: {
        label: 'Microsoft Teams',
        description: 'Hinweis im Aktivitätsfeed von Teams.',
      },
      motion: {
        label: 'Bewegung',
        description: 'Animationen reduzieren oder der Einstellung des Betriebssystems folgen.',
        options: { system: 'System', reduced: 'Reduziert' },
      },
    },
  },
  catalogs: catalogsDe,
  findings: findingsDe,
  plans: plansDe,
  documents: documentsDe,
  questionnaires: questionnairesDe,
  signature: signatureDe,
  admin: adminDe,
  company: {
    settings: {
      moduleOrder: {
        label: 'Reihenfolge der Module',
        description:
          'Reihenfolge in Seitenleiste und Startseite, festgelegt unter Verwaltung › Module.',
      },
    },
    roles: {
      'company-admin': 'Firmen-Admin',
      auditor: 'Revision',
    },
    permissions: {
      accounts: {
        read: { label: 'Konten ansehen', description: 'Liste und Details der Konten der Firma.' },
        manage: {
          label: 'Konten verwalten',
          description:
            'Konten anlegen, sperren und entsperren, Links zum Passwort senden, Authenticator-App zurücksetzen.',
        },
      },
      groups: {
        read: { label: 'Gruppen ansehen', description: 'Gruppen und ihre Mitglieder.' },
        manage: {
          label: 'Gruppen verwalten',
          description: 'Gruppen anlegen, ändern, löschen und Mitglieder pflegen.',
        },
      },
      roles: {
        read: {
          label: 'Rollen ansehen',
          description: 'Rollen, Zuweisungen und warum jemand etwas darf.',
        },
        manage: {
          label: 'Rollen verwalten',
          description: 'Rollen anlegen und ändern, Rollen zuweisen und entziehen.',
        },
      },
      modules: {
        manage: {
          label: 'Module verwalten',
          description: 'Module der Firma ein- und ausschalten.',
        },
      },
      audit: {
        read: { label: 'Audit-Log lesen', description: 'Protokoll aller Änderungen der Firma.' },
      },
      settings: {
        manage: {
          label: 'Einstellungen verwalten',
          description: 'Vorgaben der Firma für alle Personen.',
        },
      },
      branding: {
        manage: { label: 'Erscheinungsbild ändern', description: 'Logo und Firmenfarbe.' },
      },
      sso: {
        manage: {
          label: 'Anmeldung einrichten',
          description:
            'SSO-Anbieter der Firma einrichten und testen, Anmeldung mit Passwort erlauben oder beenden.',
        },
      },
      'custom-fields': {
        manage: {
          label: 'Zusatzfelder einrichten',
          description: 'Eigene Felder für Einträge der Module anlegen, ändern und löschen.',
        },
      },
      shares: {
        manage: {
          label: 'Freigaben verwalten',
          description: 'Alle Freigaben zwischen Firmen sehen und eigene beenden.',
        },
      },
      ai: {
        manage: {
          label: 'KI verwalten',
          description:
            'KI-Anbieter, eigenen API-Schlüssel, Datenstufe, Monatsgrenze und KI-Funktionen der Firma festlegen.',
        },
      },
      findings: {
        manage: {
          label: 'Status und Prioritäten der Mängel pflegen',
          description: 'Status und Prioritäten, die alle Module mit Mängeln gemeinsam nutzen.',
        },
      },
      privacy: {
        manage: {
          label: 'Datenschutz verwalten',
          description:
            'Löschfristen einschalten, Daten einer Person exportieren und Personen löschen.',
        },
      },
      integrations: {
        manage: {
          label: 'Integrationen einrichten',
          description: 'SharePoint als Ablage für Dokumente anbinden und testen.',
        },
      },
      notifications: {
        manage: {
          label: 'Benachrichtigungskanäle einrichten',
          description: 'E-Mail-Server, Teams und Push-Nachrichten der Firma einrichten und testen.',
        },
      },
    },
  },
  platform: {
    permissions: {
      superadmin: {
        label: 'Superadmin',
        description: 'Alle Rechte in jeder Firma und auf der Plattform.',
      },
      companies: {
        manage: {
          label: 'Firmen verwalten',
          description: 'Firmen und Teilgruppen anlegen, ändern und deaktivieren.',
        },
      },
      modules: {
        manage: {
          label: 'Module schalten',
          description: 'Module für alle oder einzelne Firmen freischalten.',
        },
      },
      audit: {
        read: { label: 'Plattform-Audit lesen', description: 'Protokoll der Plattformverwaltung.' },
      },
      metrics: {
        read: { label: 'Kennzahlen ansehen', description: 'Betriebskennzahlen der Plattform.' },
      },
      backups: {
        create: {
          label: 'Backups anstoßen',
          description: 'Ein Datenbank-Backup auf dem Server sofort erstellen lassen.',
        },
      },
    },
  },
};

export type Messages = typeof de;
